import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Copy, Edit, FileDown, FileText, Plus, Search } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  type TableColumnMenuItem,
} from '@/components/shared/TableColumnVisibilityMenu'
import { useAuthStore } from '@/store/auth.store'
import { deliveryChallanService } from '@/services/deliveryChallanService'

import type { DeliveryChallanRow } from '../types'

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

const DC_LIST_COLUMNS: TableColumnMenuItem[] = [
  { id: 'code', label: 'Code' },
  { id: 'customerName', label: 'Customer' },
  { id: 'orderCode', label: 'Order code' },
  { id: 'challanDate', label: 'Challan date' },
  { id: 'expectedDeliveryDate', label: 'Expected delivery' },
  { id: 'createdDate', label: 'Created date' },
  { id: 'convertInvoice', label: 'Invoice' },
  { id: 'edit', label: 'Edit' },
]

export const InventoryDeliveryChallanList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  const [rows, setRows] = useState<DeliveryChallanRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [convertingDcId, setConvertingDcId] = useState<number | null>(null)
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initialColumnVisibility(DC_LIST_COLUMNS.map((c) => c.id)),
  )

  /** New column ids default visible when state was created before those ids existed */
  const mergedVisibility = useMemo(() => {
    const m: Record<string, boolean> = {}
    for (const c of DC_LIST_COLUMNS) {
      m[c.id] = columnVisibility[c.id] !== false
    }
    return m
  }, [columnVisibility])

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    const load = async () => {
      try {
        setLoading(true)
        const res = await deliveryChallanService.getAll(branchId, pageSize, pageNumber)
        setRows(res.items)
        setTotalCount(res.totalCount)
      } catch (error: unknown) {
        setRows([])
        setTotalCount(0)
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || 'Failed to load delivery challans.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, pageSize])

  const visibleColumnCount = useMemo(
    () => DC_LIST_COLUMNS.filter((c) => mergedVisibility[c.id]).length,
    [mergedVisibility],
  )

  const toggleColumn = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentlyOn = prev[columnId] !== false
      return { ...prev, [columnId]: !currentlyOn }
    })
  }

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.customerName, r.orderCode, r.challanDate, r.expectedDeliveryDate, r.createdDate].some((x) =>
        String(x).toLowerCase().includes(q),
      ),
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  useEffect(() => {
    if (pageNumber > totalPages) setPageNumber(totalPages)
  }, [pageNumber, totalPages])

  const fmtDate = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value.includes('T') ? value : `${value}T12:00:00`)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const fmtDateTime = (value?: string) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleCopy = () => {
    if (filtered.length === 0) return toast.error('Nothing to copy.')
    const header = ['Code', 'Customer', 'Order code', 'Challan date', 'Expected delivery', 'Created date']
    const lines = filtered.map((r) =>
      [
        r.code,
        r.customerName,
        r.orderCode,
        r.challanDate,
        r.expectedDeliveryDate,
        fmtDateTime(r.createdDate),
      ].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success('Copied.')
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) return toast.error('Nothing to export.')
    const header = 'Code,Customer,OrderCode,ChallanDate,ExpectedDelivery,CreatedDate'
    const body = filtered
      .map((r) =>
        [r.code, r.customerName, r.orderCode, r.challanDate, r.expectedDeliveryDate, fmtDateTime(r.createdDate)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `delivery_challans_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  const startIdx = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const endIdx = Math.min(pageNumber * pageSize, totalCount)

  const handleConvertToInvoice = async (dcId: number) => {
    if (!branchId) return
    const row = rows.find((x) => x.id === dcId)
    if (row?.isInvoiceCreated) return
    try {
      setConvertingDcId(dcId)
      await deliveryChallanService.convertToInvoice(dcId)
      const res = await deliveryChallanService.getAll(branchId, pageSize, pageNumber)
      setRows(res.items)
      setTotalCount(res.totalCount)
      await showSuccess(
        t('deliveryChallan.convertToInvoiceSuccessTitle'),
        t('deliveryChallan.convertToInvoiceSuccess'),
        3000,
      )
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : error instanceof Error
            ? error.message
            : undefined
      toast.error(msg || t('deliveryChallan.convertToInvoiceFailed'))
    } finally {
      setConvertingDcId(null)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('navigation.inventoryDeliveryChallan')}</h1>
            <p className="text-muted-foreground text-sm">{t('deliveryChallan.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={() => navigate('/inventory/delivery-challan/new')} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('deliveryChallan.addChallan')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="w-full max-w-md">
              <label className="mb-2 block text-sm font-medium">{t('deliveryChallan.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('deliveryChallan.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TableColumnVisibilityMenu
                columns={DC_LIST_COLUMNS}
                visibility={mergedVisibility}
                onToggle={toggleColumn}
                align="end"
                minVisible={1}
                disabled={loading}
              />
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading || !branchId}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={loading || !branchId}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {mergedVisibility.code ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Code
                    </TableHead>
                  ) : null}
                  {mergedVisibility.customerName ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Customer
                    </TableHead>
                  ) : null}
                  {mergedVisibility.orderCode ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Order code
                    </TableHead>
                  ) : null}
                  {mergedVisibility.challanDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Challan date
                    </TableHead>
                  ) : null}
                  {mergedVisibility.expectedDeliveryDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Expected delivery
                    </TableHead>
                  ) : null}
                  {mergedVisibility.createdDate ? (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Created date
                    </TableHead>
                  ) : null}
                  {mergedVisibility.convertInvoice ? (
                    <TableHead className="h-12 min-w-[9rem] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('deliveryChallan.convertToInvoiceColumn')}
                    </TableHead>
                  ) : null}
                  {mergedVisibility.edit ? (
                    <TableHead className="h-12 w-[88px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Edit
                    </TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!branchId ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {t('deliveryChallan.selectBranchFirst')}
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('deliveryChallan.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Math.max(visibleColumnCount, 1)}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {t('deliveryChallan.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow
                      key={r.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {mergedVisibility.code ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.code || '-'}</TableCell>
                      ) : null}
                      {mergedVisibility.customerName ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.customerName}</TableCell>
                      ) : null}
                      {mergedVisibility.orderCode ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{r.orderCode}</TableCell>
                      ) : null}
                      {mergedVisibility.challanDate ? (
                        <TableCell className="px-4 py-2 text-center text-sm">{fmtDate(r.challanDate)}</TableCell>
                      ) : null}
                      {mergedVisibility.expectedDeliveryDate ? (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {fmtDate(r.expectedDeliveryDate)}
                        </TableCell>
                      ) : null}
                      {mergedVisibility.createdDate ? (
                        <TableCell className="px-4 py-2 text-center text-sm whitespace-nowrap">
                          {fmtDateTime(r.createdDate)}
                        </TableCell>
                      ) : null}
                      {mergedVisibility.convertInvoice ? (
                        <TableCell className="px-2 py-2 text-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 max-w-[10rem] whitespace-normal px-2 text-center text-[11px] leading-tight sm:text-xs"
                            disabled={loading || convertingDcId !== null || r.isInvoiceCreated}
                            title={
                              r.isInvoiceCreated
                                ? t('deliveryChallan.convertToInvoiceDisabledAlreadyInvoiced')
                                : t('deliveryChallan.convertToInvoice')
                            }
                            onClick={() => void handleConvertToInvoice(r.id)}
                          >
                            {convertingDcId === r.id
                              ? t('deliveryChallan.convertToInvoiceWorking')
                              : t('deliveryChallan.convertToInvoice')}
                          </Button>
                        </TableCell>
                      ) : null}
                      {mergedVisibility.edit ? (
                        <TableCell className="px-2 py-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            disabled={convertingDcId !== null}
                            onClick={() => navigate(`/inventory/delivery-challan/${r.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('deliveryChallan.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <span className="text-sm text-muted-foreground">Per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
                disabled={!branchId || loading}
              >
                <SelectTrigger className="h-8 w-[72px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent searchable={false}>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={!branchId || loading || pageNumber <= 1}
              >
                Prev
              </Button>
              <span className="min-w-[3.5rem] text-center text-sm font-medium">
                {pageNumber} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={!branchId || loading || pageNumber >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
